/**
 * QuickReplies Component
 *
 * Admin-managed quick reply chips for hosts.
 * Source of truth: Firestore `quickReplies` collection (managed in
 * Admin → Content Management → Quick Replies tab).
 *
 * Hidden when no admin-defined replies exist for hosts so the UI doesn't
 * show an empty bar.
 */

import React from 'react';
import { useQuickReplies } from '@/services/quickReplies';

interface QuickRepliesProps {
  onSelect: (content: string) => void;
  guestName?: string;
  listingTitle?: string;
}

export const QuickReplies: React.FC<QuickRepliesProps> = ({
  onSelect,
  guestName = 'there',
  listingTitle = 'the property',
}) => {
  const { replies } = useQuickReplies('host');

  if (replies.length === 0) return null;

  // Light templating: admin-defined replies can include {{guestName}} and
  // {{listingTitle}} placeholders.
  const applyVariables = (content: string): string =>
    content
      .replace(/\{\{guestName\}\}/g, guestName)
      .replace(/\{\{listingTitle\}\}/g, listingTitle);

  return (
    <div className="border-t bg-gray-50 px-4 py-3">
      <div className="flex flex-wrap gap-2">
        {replies.map((reply) => (
          <button
            key={reply.id}
            type="button"
            onClick={() => onSelect(applyVariables(reply.text))}
            className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors"
          >
            {reply.text.length > 40 ? `${reply.text.slice(0, 40)}…` : reply.text}
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuickReplies;
